import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {FormGroup} from '@angular/forms';

// models
import {
  Country,
  IncomeSource,
  Occupation,
  OnboardingEmployment,
  OnboardingRequest,
  ValidationDetail
} from '../../shared/model/models';

// services
import {FacadeService} from '../../shared/service-facade/facade.service';
import {SignupService} from "../service/signup.service";
import {Log} from "leia-api";
import {NGXLogger} from "ngx-logger";
import {Sections, SectionStates} from "../../status-tracker/model/section-status";
import {SectionStatusService} from "../../status-tracker/section-status.service";
import {AnnualIncome} from "../../shared/model/annual-income";
import {ReviewState} from "../../review/models/review-state";
import {AppValidator} from "../../shared/interfaces/AppValidator";
import {takeUntil} from "rxjs/operators";
import {Subject} from "rxjs";
import {ReviewService} from "../../review/services/review.service";
import {ComponentNameEnum} from "../../shared/model/component-name.enum";

@Component({
  selector: 'app-employment-details',
  templateUrl: './employment-details.component.html',
  styleUrls: ['./employment-details.component.scss']
})
export class EmploymentDetailsComponent implements OnInit, AppValidator, OnDestroy {
  @Input() reviewState: ReviewState = ReviewState.Initial;
  @Input() formGroup!: FormGroup;

  incomeSourceList: IncomeSource[] = [];
  occupationList: Occupation[] = [];
  countryList: Country[] = [];
  annualIncomeList: AnnualIncome[] = [];
  uneditedFormData: any;
  savedEmploymentValues: OnboardingEmployment = new OnboardingEmployment();
  shoppingCartOnboardingRequest: OnboardingRequest;
  reviewStates = ReviewState;
  validationDetail: ValidationDetail;

  destroySubject$: Subject<void> = new Subject();

  get isPrimary(): boolean {
    return this.formGroup.value.isPrimary;
  }

  constructor(
              private facadeService: FacadeService,
              private signupService: SignupService,
              private statusTrackerService: SectionStatusService,
              private logger: NGXLogger,
              private reviewService: ReviewService
  ) {
    this.shoppingCartOnboardingRequest = new OnboardingRequest();
    this.validationDetail = new ValidationDetail(this.formGroup, new Map<string, string>());
  }

  ngOnInit(): void {
    this.populateLists();

    // Listen to posible review data being pushed from verification procedure
    this.facadeService.shoppingCartOnboardingRequest$
      .pipe(
        takeUntil(this.destroySubject$)
      ).subscribe(() => {
        // this.setupData();
      });

    this.formGroup.valueChanges.pipe(takeUntil(this.destroySubject$)).subscribe(value => {
      this.signupService.formElementHasChanged = true;
    });
  }

  ngOnDestroy(): void {
    this.destroySubject$.next();
    if (!this.formGroup.value.isPrimary) {
      this.clearValidations();
    }
  }

  setupValidationTrigger(): void {
    this.statusTrackerService.onTriggerValidations.pipe(takeUntil(this.destroySubject$)).subscribe(() => {
      this.formGroup.markAllAsTouched();
      this.formGroup.updateValueAndValidity();
    });
  };

  /**
   * Setup the initial data for the component.
   */
  setupData() {
    this.shoppingCartOnboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();
  }

  /**
   * Call to update validations in the validations component.
   */
  updateValidations(): void {
    this.signupService.validationsChange.emit(this.validationDetail);
  }

  clearValidations(): void {
    this.signupService.validationsChange.emit(
      new ValidationDetail(this.formGroup, this.validationDetail.labels, true)
    );
  }

  getFieldError(key: string): any {
    return this.validationDetail.labels.get(key) + " is required.";
  }

  /**
   * Setup validation map for labels.
   */
  setupValidationMap(): void {
    const { applicantType } = this.formGroup.value;
    let labelMap = new Map<string, string>();
    labelMap.set("incomeSource", "Primary source of income");
    labelMap.set("occupation", "Occupation");
    labelMap.set("employerCountry", "Employer country");
    labelMap.set("annualIncome", "Annual income");
    this.validationDetail = new ValidationDetail(this.formGroup, labelMap);
  }

  /***
   * load the shopping cart/onboarding values and set defaults if in development mode
   */
  loadShoppingCartValues(): void {
    // load the onboarding request
    this.shoppingCartOnboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();

    // save the initial values so that they can be reset
    this.save();
  }

  /***
   *  Get status changes for form and update status tracker.
   */
  setupStatusTracker(): void {
    this.formGroup.statusChanges.pipe(takeUntil(this.destroySubject$)).subscribe(validChange => {
      this.logger.debug("Employment Details- Status Change:" + validChange);
      let isDirty = this.formGroup.dirty;
      this.updateValidations();
    });
  }

  onSubmit() {
    this.logger.debug("employment-details submit")
    let logs: Log[] = [];

    let log = new Log();
    log.source = "employment-details.component";
    log.timestamp = new Date().toISOString();
    logs.push(log);

    // load the onboarding request
    this.shoppingCartOnboardingRequest = this.facadeService.getShoppingCartOnboardingRequest();

    this.logger.debug("EmploymentDetailsComponent:onSubmit - Finished ");
  }

  /***
   * populate the list values
   */
  populateLists(): void {
    this.countryList = this.facadeService.getCountries();
    this.annualIncomeList = this.facadeService.getAnnualIncomes();
    this.annualIncomeList.sort((a,b) => a.minValue > b.minValue ? 1 : -1);
    this.occupationList = this.facadeService.getAllOccupations();

    this.incomeSourceList = this.facadeService.getAllIncomeSources();

    this.setupStatusTracker();
    this.setupValidationMap();
    this.setupValidationTrigger();
  }

  /**
   * Put the view in edit mode
   */
  editDetails() {
    this.uneditedFormData = this.formGroup.value;
    this.reviewState = ReviewState.Edit;

    this.reviewService.editStateArray.push([ComponentNameEnum.EmploymentDetails.toString(), this.isPrimary? 0 : 1])
    this.reviewService.editStateMapChanged.emit();
  }

  /**
   * Save the edited fields and set back to readonly
   */
  save() {
    if(!this.formGroup.valid) return;

    this.reviewState = ReviewState.ReadOnly;

    const applicantIndex = this.isPrimary? 0 : 1;
    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.EmploymentDetails.toString() && val[1] === applicantIndex), 1)
    this.reviewService.editStateMapChanged.emit();
  }

  /**
   * Cancel edit mode and restore fields to previous values.
   */
  cancel() {
    this.formGroup.patchValue(this.uneditedFormData, {emitEvent: false});
    this.reviewState = ReviewState.ReadOnly;

    const applicantIndex = this.isPrimary? 0 : 1;
    this.reviewService.editStateArray.splice(this.reviewService.editStateArray.findIndex(val => val[0] === ComponentNameEnum.EmploymentDetails.toString() && val[1] === applicantIndex),1)
    this.reviewService.editStateMapChanged.emit();
  }

  incomeSourceTouched(): void {
    this.formGroup.get("incomeSource")?.markAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  occupationTouched(): void {
    this.formGroup.get("occupation")?.markAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  employerCountryTouched(): void {
    this.formGroup.get("employerCountry")?.markAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  annualIncomeTouched(): void {
    this.formGroup.get("annualIncome")?.markAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  taxPayerCertificationCheckboxTouched(): void {
    this.formGroup.get("taxPayerCertificationCheckbox")?.markAsTouched();
    this.formGroup.updateValueAndValidity();
  }

  getIncomeSourceDescription(): string {
    const { incomeSource } = this.formGroup.value;

    if (incomeSource === -1) {
      return "";
    }

    return this.incomeSourceList.find(item => item.incomeSourceId === incomeSource)!.incomeSourceName;
  }

  getOccupationDescription(): string {
    const { occupation } = this.formGroup.value;

    if (occupation === -1) {
      return "";
    }

    return this.occupationList.find(item => item.occupationId === occupation)!.occupationName;
  }

  getCountryDescription(): string {
    const { employerCountry } = this.formGroup.value;

    if (employerCountry === -1) {
      return "";
    }
    return this.countryList.find(item => item.countryId === employerCountry)!.countryName;
  }

  getAnualIncomeDescription(): string {
    const { annualIncome } = this.formGroup.value;

    if (annualIncome === -1) {
      return "";
    }

    return this.annualIncomeList.find(item => item.annualIncomeId === annualIncome)!.displayValue;
  }

  compareValueWithSelection(optionValue: any, selectionValue: any): boolean {
    return optionValue === selectionValue;
  }
}
